import { FormSectionHandler } from '../entity.handler';
import { UserResponse, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, TextField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { AuthService } from 'app/services';
import QRCode from "qrcode";

interface EditorValues {
    types: ListFieldValue<IdName>[];
}

export class UserOtpSectionHandler implements FormSectionHandler<UserResponse, EditorValues> {

    type = 'form';
    icon = 'key';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    userId: string;
    buttons = ['ok'];
    modified = false;

    actions = [
        { id: 'clear', name: this.translate('CLEAR_TOKENS'), showAlways: true },
    ];

    constructor(
        public admin: AdminEditorService,
        public auth: AuthService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.USER.OTP.${key}`, values);
    }

    async applyUpdate(entity: UserResponse, _payload: any): Promise<UserResponse> {
        // we don't support saving at the end in this handler, just get the latest version of the entity
        return this.modified ? await this.admin.getCached(`getUser/${entity.id}`, api => api.accounts.getUser(entity.id), 0) : entity;
    }

    async mapEntityToEditor(entity: UserResponse): Promise<EditorValues> {
        this.userId = entity.id;

        // only show this for the current user
        if (entity.id !== this.admin.user.id) {
            this.actions = [];
        }

        return {
            types: (entity.otp || []).map(item => this.itemToListItem(item.type, { id: item.type, name: this.translate('TYPES.' + item.type.toUpperCase()) })),
        };
    }

    async mapEditorToEntity(_entity: any, _values: EditorValues): Promise<any> {
        return {}; // we don't support saving
    }

    async getFields(entity: UserResponse): Promise<FormBuilderField[]> {


        // check that our password policy includes TOTP before allowing a user to enrol
        const company = await this.admin.getCached(`getCompany/${this.ownerId}`, api => api.accounts.getCompany(this.ownerId));
        const canAdd = Object.keys(company.passwordPolicy?.otpSettings?.methods || {}).includes('totp');

        const fields: FormBuilderField[] = [
            {
                id: 'types', title: this.translate('FIELDS.TYPES'), type: 'list', required: false,
                actions: [
                    entity.id === this.admin.user.id && !entity.otp?.find(x => x.type === 'totp') && canAdd && { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddTotpModal.bind(this) },
                    entity.id === this.admin.user.id && { id: 'edit', click: this.showAddTotpModal.bind(this) },
                    { id: 'delete', click: this.deleteItem.bind(this) },
                ].filter(x => x),
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ];
        return EntityUtils.prepareBulk(!entity, [], fields);
    }


    async deleteItem(_action: any, _field: FormBuilderField, _values: any, item?: ListFieldValue<IdName>): Promise<boolean> {
        try {
            const result = await this.admin.modal.confirm(this.translate('MODALS.DELETE.DESC'));
            if (result) {
                this.modified = true;
                await this.admin.applyUpdate(api => api.accounts.deleteOTPMethod(this.userId, item.id));
            }
            return result;
        } catch (err) {
            await this.admin.modal.error(err.message);
            return false;
        }
    }

    itemToListItem(id: string, item: IdName): ListFieldValue<IdName> {
        return {
            id: id,
            name: item.name,
            description: id,
            icon: 'key',
            data: item,
        };
    }

    updateOrAddItem(items: ListFieldValue<IdName>[], id: string, item: IdName) {
        
        const idx = items.findIndex(x => x.id === id);
        if (idx > -1) {
            items[idx] = this.itemToListItem(id, item);
        } else {
            items.push(this.itemToListItem(id, item));
        }
       
    }

    async actionTriggered(entity: UserResponse, action: string): Promise<void> {
        if (action === 'clear') {
            const result = await this.admin.modal.confirm(this.translate('MODALS.CLEAR.DESC'));
            if (result) {
                this.auth.clearOtpToken(entity.id);
            }
        }
    }


    async showAddTotpModal(_action: string, _field: FormBuilderField, values: EditorValues): Promise<IdName> {

        try {
        
        const result = await this.admin.applyUpdate(api => api.auth.enrolOTP('totp'));
        const qrCode = await QRCode.toDataURL(result.totpAuthUrl);

        // clear any existing OTP tokens
        this.auth.clearOtpToken(this.userId);

            const details = await this.admin.showFormModal<any>(
                {
                    groups: [{
                        name: this.translate('MODALS.ADD_TOTP.TITLE'),
                        description: this.translate('MODALS.ADD_TOTP.DESC'),
                        fields: [
                            { id: 'qrcode', title: this.admin.i18n.instant('OTP.QR_CODE'), type: 'image', width: 228, readonly: true },
                            { id: 'secret', title: this.admin.i18n.instant('OTP.SECRET'), type: 'text', readonly: true },
                            TextField('token', this.admin.i18n.instant('OTP.TOKEN'), true, 1, 255),
                        ],
                    }],
                }, { id: 'totp', token: '', secret: result.secret, qrcode: { preview: qrCode } }
            );
            if (details) {
                try {
                    await this.admin.applyUpdate(api => api.auth.verifyOTP('totp', { code: details.token }));
                } finally {
                    this.updateOrAddItem(values.types, 'totp', {
                        id: 'totp',
                        name: this.translate('TYPES.TOTP')
                    });
                }
            }
        } catch (err) {
            await this.admin.modal.error(err.message);
            return null;
        }

    }

}

